"use client"

import { useEffect, useState, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Card, Button, Checkbox, Alert, Spin, Typography, Space, Modal } from "antd"
import { FileTextOutlined, CheckCircleOutlined } from "@ant-design/icons"
import { getDocuments } from "../services/DocumentsServices"
import { acceptUserTerms } from "../services/UserServices"
import { useAuth } from "../contexts/authContext"
import "../assets/styles/AcceptTerms.css"

const REACT_APP_BFF_URL = process.env.REACT_APP_BFF_URL

const { Title, Paragraph } = Typography

const AcceptTerms = () => {
  const [document, setDocument] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [hasReachedBottom, setHasReachedBottom] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [showWarning, setShowWarning] = useState(false)
  const pdfContainerRef = useRef(null)
  const navigate = useNavigate()
  const { getToken, currentUser } = useAuth()

  // Determinar el rol del usuario para obtener el documento correspondiente
  const userRole = currentUser?.photoURL === "admin" ? "admin" : currentUser?.photoURL || "cliente"

  useEffect(() => {
    const fetchTermsDocument = async () => {
      try {
        setLoading(true)
        const token = await getToken()

        if (!token) {
          throw new Error("No se encontró token de autenticación")
        }

        // Obtener el documento de términos según el rol del usuario
        const docs = await getDocuments(token, userRole)
        console.log("Documentos obtenidos:", docs)

        // Usar el primer documento disponible para el rol del usuario
        // En un entorno de producción, deberías tener una forma más específica de identificar
        // el documento de términos y condiciones
        if (!docs || docs.length === 0) {
          throw new Error("No se encontraron documentos para este rol de usuario")
        }

        // Usar el primer documento disponible
        setDocument(docs[0])
      } catch (error) {
        console.error("Error al cargar el documento de términos:", error)
        setError("No se pudo cargar el documento de términos y condiciones. Por favor, inténtelo más tarde.")
      } finally {
        setLoading(false)
      }
    }

    fetchTermsDocument()
  }, [getToken, userRole])

  // Función para detectar cuando el usuario ha llegado al final del documento
  const handleScroll = () => {
    if (!pdfContainerRef.current) return

    const { scrollTop, scrollHeight, clientHeight } = pdfContainerRef.current

    // Consideramos que ha llegado al final si está a menos de 20px del final
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 20

    if (isAtBottom && !hasReachedBottom) {
      setHasReachedBottom(true)
    }
  }

  // Función para manejar la aceptación de términos
  const handleAcceptTerms = async () => {
    if (!termsAccepted) {
      setShowWarning(true)
      return
    }

    try {
      setSubmitting(true)
      const token = await getToken()

      if (!token) {
        throw new Error("No se encontró token de autenticación")
      }

      await acceptUserTerms(token)

      // Mostrar mensaje de éxito y redirigir
      Modal.success({
        title: "Términos aceptados",
        content: "Has aceptado los términos y condiciones correctamente.",
        onOk: () => navigate("/"),
      })
    } catch (error) {
      console.error("Error al aceptar los términos:", error)
      setError("No se pudieron registrar los términos aceptados. Por favor, inténtelo más tarde.")
      setSubmitting(false)
    }
  }

  // Función para manejar el cambio en el checkbox
  const handleCheckboxChange = (e) => {
    setTermsAccepted(e.target.checked)
    if (e.target.checked) {
      setShowWarning(false)
    }
  }



  if (loading) {
    return (
      <div className="terms-loading-container">
        <Spin size="large" />
        <p>Cargando documento de términos y condiciones...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="terms-error-container">
        <Alert message="Error" description={error} type="error" showIcon />
        <Button type="primary" onClick={() => navigate("/")} style={{ marginTop: 16 }}>
          Volver al inicio
        </Button>
      </div>
    )
  }

  return (
    <div className="accept-terms-container">
      <Card className="terms-card">
        <Title level={2} className="terms-title">
          <FileTextOutlined /> Términos y Condiciones
        </Title>

        <Paragraph className="terms-intro">
          Por favor, lea detenidamente los siguientes términos y condiciones. Para continuar utilizando nuestra
          plataforma, debe aceptar estos términos desplazándose hasta el final del documento y marcando la casilla de
          aceptación.
        </Paragraph>

        {document ? (
          <div className="pdf-container" ref={pdfContainerRef} onScroll={handleScroll}>
            <iframe
              src={`${REACT_APP_BFF_URL}/api/documents/${document.id}/preview`}
              width="100%"
              height="100%"
              style={{ border: "none" }}
              title="Términos y Condiciones"
            />
          </div>
        ) : (
          <Alert
            message="Documento no disponible"
            description="No se pudo cargar el documento de términos y condiciones."
            type="warning"
            showIcon
          />
        )}

        <div className="terms-actions">
          {!hasReachedBottom && (
            <Alert
              message="Desplácese hasta el final del documento"
              description="Debe leer todo el documento antes de poder aceptar los términos y condiciones."
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />
          )}

          <Checkbox
            checked={termsAccepted}
            onChange={handleCheckboxChange}
            disabled={!hasReachedBottom}
            className="terms-checkbox"
          >
            He leído y acepto los términos y condiciones
          </Checkbox>

          {showWarning && (
            <Alert
              message="Debe aceptar los términos y condiciones para continuar"
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          <Space className="terms-buttons">
            <Button type="default" onClick={() => navigate("/")} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              type="primary"
              onClick={handleAcceptTerms}
              loading={submitting}
              icon={<CheckCircleOutlined />}
              disabled={!hasReachedBottom}
            >
              Aceptar y Continuar
            </Button>
          </Space>
        </div>
      </Card>
    </div>
  )
}

export default AcceptTerms
